/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   TrendSparkline.tsx
 *     Description     :   Sparkline chart component for 7-day capacity utilization trends
 *     Author          :   Peter Dobler
 *     Created         :   2026-01-19
 *     Modified        :   2026-01-19 | Peter Dobler | Initial implementation
 *                         2026-06-15 | Migrated off MUI onto the token-driven kit (Spec 197)
 *     Constitution    :   v1.11.0 (Specification Testing Standards Edition)
 *     Feature         :   030-capacity-metrics (T063)
 * =========================================================================================
 */

import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  ResponsiveContainer,
  ReferenceLine,
  Tooltip,
  XAxis,
} from 'recharts';
import type { SparklineDataPoint } from '../../../../shared/types/capacityMetrics';
import { formatEastern } from '@/utils/dateFormatter';

// ============================================================================
// Token colors (CSS variables resolve in SVG stroke/fill)
// ============================================================================

const TOKEN = {
  accent: 'var(--color-accent)',
  danger: 'var(--color-status-danger)',
  warning: 'var(--color-status-warning)',
  success: 'var(--color-status-success)',
  border: 'var(--color-border)',
  card: 'var(--color-bg-card)',
} as const;

// ============================================================================
// Types
// ============================================================================

interface TrendSparklineProps {
  data: SparklineDataPoint[];
  width?: number;
  height?: number;
  showThresholds?: boolean;
  warningThreshold?: number;
  criticalThreshold?: number;
}

// ============================================================================
// Tooltip Component
// ============================================================================

const CustomTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const dataPoint = payload[0].payload;
  const date = new Date(dataPoint.timestamp);

  return (
    <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 py-2 shadow-[var(--shadow-md)]">
      <span className="block text-xs text-[var(--color-text-secondary)]">
        {formatEastern(date)}
      </span>
      <span className="text-sm font-semibold text-[var(--color-text-primary)]">
        {dataPoint.value.toFixed(1)}%
      </span>
    </div>
  );
};

// ============================================================================
// Sparkline Component
// ============================================================================

const TrendSparkline: React.FC<TrendSparklineProps> = ({
  data,
  width = 120,
  height = 40,
  showThresholds = false,
  warningThreshold = 80,
  criticalThreshold = 95,
}) => {
  // Transform data for recharts
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map((point) => ({
      timestamp: new Date(point.timestamp).getTime(),
      value: point.value,
    }));
  }, [data]);

  // Determine line color based on current value
  const lineColor = useMemo(() => {
    if (!chartData.length) return TOKEN.accent;

    const lastValue = chartData[chartData.length - 1]?.value || 0;

    if (lastValue >= criticalThreshold) {
      return TOKEN.danger;
    } else if (lastValue >= warningThreshold) {
      return TOKEN.warning;
    }
    return TOKEN.success;
  }, [chartData, warningThreshold, criticalThreshold]);

  // Empty state
  if (!chartData || chartData.length === 0) {
    return (
      <div
        className="flex items-center justify-center"
        style={{ width, height }}
      >
        <span className="text-xs text-[var(--color-text-secondary)]">No data</span>
      </div>
    );
  }

  return (
    <div style={{ width, height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
          {/* Hidden X-axis for tooltip positioning */}
          <XAxis dataKey="timestamp" hide />

          {/* Threshold reference lines */}
          {showThresholds && (
            <>
              <ReferenceLine
                y={warningThreshold}
                stroke={TOKEN.warning}
                strokeDasharray="2 2"
                strokeWidth={1}
              />
              <ReferenceLine
                y={criticalThreshold}
                stroke={TOKEN.danger}
                strokeDasharray="2 2"
                strokeWidth={1}
              />
            </>
          )}

          {/* Tooltip */}
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ stroke: TOKEN.border, strokeWidth: 1 }}
          />

          {/* Main line */}
          <Line
            type="monotone"
            dataKey="value"
            stroke={lineColor}
            strokeWidth={1.5}
            dot={false}
            activeDot={{
              r: 3,
              stroke: lineColor,
              strokeWidth: 2,
              fill: TOKEN.card,
            }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TrendSparkline;
